import { formatDate } from '../../../utils';
import { useDataFetch } from '../../../hooks/use-data-fetch';
import { useUser } from '../../../context/UserContext';
import { useMultiCurrencyBonus, CurrencyTotals, DatePeriod } from '../../../hooks/use-multi-currency-bonus';

interface WeekPeriod extends DatePeriod {}

interface InfluencerBonusData {
  totalInfluencerBonusByLevel: CurrencyTotals;
  weekPeriod: WeekPeriod;
}

interface InfluencerBonusResponse {
  message: string;
  data: InfluencerBonusData;
  errors: null | any;
}

export const useInfluencerBonus = () => {
  const { getData } = useDataFetch();
  const { userData} = useUser()
  
  // Use the generic multi-currency bonus hook
  const {
    calculateActiveCurrencies,
    calculateProgressPercentage,
    calculateDaysRemaining,
    isCurrentPeriod,
    countriesLoading,
    userCurrency
  } = useMultiCurrencyBonus();

  const {
    data: bonusData,
    isLoading,
    error,
    refetch
  } = getData(
    ['influencer-bonus-weekly'],
    '/order/calculate/weekly/influencer_bonus',
    {
      enabled: true
    }
  );

  // Parse and format the data
  const parsedData: InfluencerBonusResponse | null = bonusData || null;
  
  // Get currencies with values > 0
  const getActiveCurrencies = () => {
    if (!parsedData?.data?.totalInfluencerBonusByLevel) {
      // If no data, return empty array to show no currencies
      return [];
    }
    
    // Use the generic function to calculate active currencies
    const activeCurrencies = calculateActiveCurrencies(parsedData.data.totalInfluencerBonusByLevel);
    
    // Map to the expected format for this hook
    return activeCurrencies.map(curr => ({
      currency: curr.currency,
      amount: curr.totalPoints,
      value: curr.totalValue
    }));
  };
  
  // Calculate total earned across all currencies
  const getTotalEarned = () => {
    if (!parsedData?.data?.totalInfluencerBonusByLevel) return 0;
    
    return Object.values(parsedData.data.totalInfluencerBonusByLevel)
      .reduce((total, amount) => total + amount, 0);
  };
  
  // Calculate total earned value across all currencies
  const getTotalEarnedValue = () => {
    const activeCurrencies = getActiveCurrencies();
    return activeCurrencies.reduce((total, curr) => total + curr.value, 0);
  };
  
  const getProgressPercentage = (): number => {
    if (!parsedData?.data.weekPeriod) return 0;
    return calculateProgressPercentage(parsedData.data.weekPeriod);
  };

  const getDaysRemaining = (): number => {
    if (!parsedData?.data.weekPeriod) return 0;
    return calculateDaysRemaining(parsedData.data.weekPeriod);
  };

  return {
    // Raw data
    bonusData: parsedData?.data || null,
    isLoading: isLoading || countriesLoading,
    error,
    refetch,
    
    // Computed values
    totalEarned: getTotalEarned(),
    totalEarnedValue: getTotalEarnedValue(),
    activeCurrencies: getActiveCurrencies(),
    currency: userCurrency,
    weekPeriod: parsedData?.data.weekPeriod || null,
    startDate: parsedData?.data.weekPeriod ? formatDate(parsedData.data.weekPeriod.start) : '',
    endDate: parsedData?.data.weekPeriod ? formatDate(parsedData.data.weekPeriod.end) : '',
    progressPercentage: getProgressPercentage(),
    daysRemaining: getDaysRemaining(),
    
    // Status
    isCurrentWeek: parsedData?.data.weekPeriod ? 
      isCurrentPeriod(parsedData.data.weekPeriod) : false,
    
    // Feature flags
    influencerBonusEnabled: userData?.network?.influencer_bonus_status ?? false,
  };
};
